import { useEffect, useState } from 'react'
import {
  ArrowLeft,
  BookOpen,
  FolderOpen,
  Home,
  ScrollText,
  Shield,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'
import { cn } from '@/shared/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const NAV_ROW = cn(
  buttonVariants({ variant: 'ghost' }),
  'inline-flex h-9 w-full items-center justify-start gap-2 px-3 font-normal lg:w-full'
)

const NAV_ITEMS = [
  { path: '', labelKey: 'nav.introduction', icon: Home },
  { path: 'risk-assessment', labelKey: 'nav.riskAssessment', icon: Shield },
  { path: 'user-information', labelKey: 'nav.userInformation', icon: BookOpen },
  {
    path: 'declaration-of-conformity',
    labelKey: 'nav.declarationOfConformity',
    icon: ScrollText,
  },
  {
    path: 'technical-documentation',
    labelKey: 'nav.technicalDocumentation',
    icon: FolderOpen,
  },
] as const

function buildHref(studioBaseHref: string, path: string) {
  return path.length > 0 ? `${studioBaseHref}/${path}` : studioBaseHref
}

export function CraStudioNav({
  studioBaseHref,
  backHref,
  productName,
}: {
  studioBaseHref: string
  backHref: string
  productName: string
}) {
  const { t } = useTranslation('craCompliance')
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const baseHref = buildHref(studioBaseHref, '')
  const items = NAV_ITEMS.map((item) => {
    const href = buildHref(studioBaseHref, item.path)
    const isActive =
      item.path.length === 0
        ? pathname === href
        : pathname === href || pathname.startsWith(`${href}/`)
    return { ...item, href, isActive, title: t(item.labelKey) }
  })
  const selectValue = items.find((item) => item.isActive)?.href ?? baseHref
  const [val, setVal] = useState(selectValue)

  useEffect(() => {
    setVal(selectValue)
  }, [selectValue])

  return (
    <div className='flex flex-col gap-2'>
      <div className='px-1 pt-1 md:px-1 md:pt-0'>
        <Link
          to={backHref}
          className={cn(NAV_ROW, 'text-muted-foreground hover:text-foreground')}
        >
          <ArrowLeft className='size-4 shrink-0 rtl:rotate-180' aria-hidden />
          <span className='min-w-0 flex-1 truncate text-start text-xs font-medium'>
            {productName}
          </span>
        </Link>
      </div>

      <div className='md:hidden'>
        <Select
          value={val}
          onValueChange={(value) => {
            if (!value) {
              return
            }
            setVal(value)
            navigate(value)
          }}
          items={items.map((item) => ({
            value: item.href,
            label: item.title,
          }))}
        >
          <SelectTrigger className='w-full'>
            <SelectValue placeholder={t('nav.introduction')} />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => {
              const Icon = item.icon
              return (
                <SelectItem key={item.href} value={item.href}>
                  <div className='flex gap-x-4 px-2 py-1'>
                    <span className='scale-125'>
                      <Icon size={18} />
                    </span>
                    <span className='text-md'>{item.title}</span>
                  </div>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea
        orientation='horizontal'
        type='always'
        className='hidden w-full min-w-40 bg-background px-1 py-2 md:block'
      >
        <nav className='flex space-x-2 py-1 lg:flex-col lg:space-y-1 lg:space-x-0'>
          {items.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.path || 'overview'}
                to={item.href}
                className={cn(
                  NAV_ROW,
                  item.isActive
                    ? 'bg-muted text-foreground hover:bg-accent'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <span className='shrink-0'>
                  <Icon size={18} />
                </span>
                <span className='min-w-0 flex-1 truncate text-start text-xs font-medium'>
                  {item.title}
                </span>
              </Link>
            )
          })}
        </nav>
      </ScrollArea>
    </div>
  )
}
